import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, MagnifyingGlass, MapPin, Calendar, House, Package as PackageIcon,
  Truck, Warning, CheckCircle, CurrencyInr,
} from "@phosphor-icons/react";

const PRIORITIES = [
  { key: "NORMAL", label: "Normal", tone: "#2D5A27",
    desc: "Regular collection on your scheduled date · ₹0" },
  { key: "HIGH", label: "High", tone: "#C25D38",
    desc: "Priority — admin will confirm date · surcharge applies" },
  { key: "EMERGENCY", label: "Emergency", tone: "#9F1239",
    desc: "Same-day / next-day — admin will confirm · max surcharge" },
];

export default function ServiceRequestForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();

  // Iter 101 — allow deep-link from Tickets / Chats to prefill the form.
  const initialQuery = React.useMemo(() => {
    if (typeof window === "undefined") return {};
    const sp = new URLSearchParams(window.location.search);
    return {
      customer_id: sp.get("customer_id") || "",
      description: sp.get("description") || sp.get("issue") || "",
      priority: (sp.get("priority") || "").toUpperCase(),
      source_ticket_id: sp.get("source_ticket_id") || "",
      source_chat_id:   sp.get("source_chat_id") || "",
    };
  }, []);

  const [customers, setCustomers] = useState([]);
  const [lastServiceMap, setLastServiceMap] = useState({});  // Iter 132 — customer_id → {days_since_last_service}
  const [custSearch, setCustSearch] = useState("");
  const [autofill, setAutofill] = useState(null);
  const [specialDates, setSpecialDates] = useState([]);
  const [categories, setCategories] = useState([]); // [{category, types:[]}]
  const [packages, setPackages] = useState([]);
  const [extraCharges, setExtraCharges] = useState([]); // [{id, type, amount, priority}]
  const [bill, setBill] = useState(null);
  const [altLocations, setAltLocations] = useState([]);
  const [form, setForm] = useState({
    customer_id: initialQuery.customer_id || "",
    service_category_type: "home_service",
    waste_category: "",
    priority: ["NORMAL","HIGH","EMERGENCY"].includes(initialQuery.priority)
              ? initialQuery.priority : "NORMAL",
    package_id: "",
    preferred_date: "",
    preferred_slot: "",
    description: initialQuery.description || "",
    service_category: "bulk_pickup",
    estimated_weight_kg: "",
    override_date: false,
    extra_charge_ids: [],
    source_ticket_id: initialQuery.source_ticket_id || null,
    source_chat_id:   initialQuery.source_chat_id || null,
    allow_partners: null,   // null = use tenant default
    is_urgent: false,
  });
  const [busy, setBusy] = useState(false);

  const isDrop = form.service_category_type === "drop";
  const isHighOrEmergency = !isDrop && (form.priority === "HIGH" || form.priority === "EMERGENCY");

  // Initial loads — each independent so one slow call can't blank the others.
  useEffect(() => {
    const load = (p, fn) => p.then(fn).catch(e => toast.error(formatApiError(e)));
    load(api.get("/service-request-helpers/waste-categories"), r => setCategories(r.data || []));
    load(api.get("/charges"), r => setExtraCharges(r.data || []));
    api.get("/service-request-helpers/alternate-locations?days=14&service_category_type=drop")
      .then(r => setAltLocations(r.data?.items || [])).catch(() => {});
    api.get("/service-request-helpers/customer-last-service").then(r => setLastServiceMap(r.data || {})).catch(() => {});
    if (isEdit) {
      api.get(`/service-requests/${id}`).then(r => {
        const d = r.data;
        setForm(f => ({
          ...f,
          customer_id: d.customer_id,
          service_category: d.service_category,
          service_category_type: d.service_category_type || "on_premises",
          waste_category: d.waste_category || "",
          preferred_date: d.preferred_date,
          preferred_slot: d.preferred_slot || "",
          priority: d.priority,
          package_id: d.package_id,
          description: d.description || "",
          estimated_weight_kg: d.estimated_weight_kg || "",
          override_date: d.date_override || false,
        }));
      }).catch(e => toast.error(formatApiError(e)));
    }
  }, [isEdit, id]);

  // Customer → autofill
  useEffect(() => {
    if (!form.customer_id) { setAutofill(null); setSpecialDates([]); return; }
    api.get(`/service-requests-autofill/${form.customer_id}`)
      .then(r => {
        setAutofill(r.data);
        setForm(f => ({
          ...f,
          preferred_date: f.preferred_date || r.data.next_collection_date || "",
        }));
      }).catch(e => toast.error(formatApiError(e)));
    // Fetch any upcoming special-collection dates for this customer's zone
    // — surfaces as an info banner so the office user can schedule the SR
    // on that date when appropriate.
    api.get(`/customers/${form.customer_id}/special-collection-dates`)
      .then(r => setSpecialDates(r.data?.rules || []))
      .catch(() => setSpecialDates([]));
  }, [form.customer_id]);

  // Waste category + service type → filtered packages (server-side filter)
  useEffect(() => {
    const params = new URLSearchParams();
    if (form.waste_category) params.set("waste_category", form.waste_category);
    params.set("service_category_type", form.service_category_type);
    api.get(`/service-request-helpers/packages?${params.toString()}`)
      .then(r => setPackages(r.data || []))
      .catch(() => setPackages([]));
  }, [form.waste_category, form.service_category_type]);

  // Live bill preview
  useEffect(() => {
    if (!form.package_id) { setBill(null); return; }
    const ctrl = new AbortController();
    api.post("/service-requests/preview-bill", {
      customer_id: form.customer_id || null,
      package_id: form.package_id,
      service_category_type: form.service_category_type,
      priority: form.priority,
      extra_charge_ids: form.extra_charge_ids,
    }, { signal: ctrl.signal })
      .then(r => setBill(r.data))
      .catch(() => {});
    return () => ctrl.abort();
  }, [form.package_id, form.service_category_type, form.priority,
      form.extra_charge_ids, form.customer_id]);

  const [custLoading, setCustLoading] = useState(false);
  useEffect(() => {
    const q = custSearch.trim();
    if (!q) { setCustomers([]); return; }
    let live = true;
    setCustLoading(true);
    const t = setTimeout(() => {
      api.get("/customers", { params: { q, limit: 30, sort_by: "name" } })
        .then(r => live && setCustomers(r.data || []))
        .catch(e => live && toast.error(formatApiError(e)))
        .finally(() => live && setCustLoading(false));
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [custSearch]);
  const filteredCustomers = customers;

  const isHighOrUrgent = isHighOrEmergency;
  const noSchedule = !!autofill && (autofill.next_5_dates?.length || 0) === 0;
  const missingZone = !!autofill && !autofill.zone_name;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (!form.package_id) { toast.error("Pick a package"); return; }
    if (!isHighOrUrgent && !form.preferred_date) {
      toast.error("Pick a collection date"); return;
    }
    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        service_category: form.service_category,
        service_category_type: form.service_category_type,
        waste_category: form.waste_category || null,
        preferred_date: isHighOrUrgent ? null : (form.preferred_date || null),
        preferred_slot: form.preferred_slot || null,
        priority: form.priority,
        package_id: form.package_id,
        description: form.description || null,
        estimated_weight_kg: form.estimated_weight_kg
          ? Number(form.estimated_weight_kg) : null,
        photos: [],
        override_date: !!form.override_date,
        allow_partners: form.allow_partners,
        is_urgent: !!form.is_urgent,
      };
      if (isEdit) await api.put(`/service-requests/${id}`, payload);
      else await api.post("/service-requests", payload);
      toast.success(isEdit ? "Updated" : "Request created");
      nav("/operations/service-requests");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <AppShell title={isEdit ? "Edit Service Request" : "New Service Request"}
      subtitle="Operations · step-by-step"
      actions={
        <button onClick={() => nav("/operations/service-requests")}
                className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
          <ArrowLeft size={14}/>Back
        </button>
      }>
      <form onSubmit={submit} className="max-w-4xl mx-auto space-y-6" data-testid="sr-form">

        {/* STEP 1 — CUSTOMER */}
        <Step n={1} title="Customer" done={!!form.customer_id}>
          {!form.customer_id && (
            <>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
                <input data-testid="sr-cust-search" placeholder="Search by name / code / phone"
                       value={custSearch} onChange={e => setCustSearch(e.target.value)}
                       className="w-full h-10 pl-8 pr-3 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>
              {custSearch && (
                <div className="mt-2 max-h-56 overflow-y-auto border border-[#E5E2D9] rounded-md bg-white">
                  {custLoading && <div className="px-3 py-2 text-xs text-[#8A8780]" data-testid="sr-cust-searching">Searching…</div>}
                  {!custLoading && filteredCustomers.length === 0 && <div className="px-3 py-2 text-xs text-[#8A8780]">No matches</div>}
                  {filteredCustomers.map(c => {
                    const ls = lastServiceMap[c.id] || {};
                    const days = ls.days_since_last_service;
                    return (
                      <button key={c.id} type="button" data-testid={`cust-opt-${c.id}`}
                              onClick={() => { setForm(f => ({...f, customer_id: c.id})); setCustSearch(""); }}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-[#F7F6F2] border-b border-[#F7F6F2]">
                        <div className="font-medium flex items-center gap-2">
                          <span>{c.name}</span>
                          <span className="font-mono text-[#8A8780]">{c.customer_code}</span>
                          {days != null && (
                            <span data-testid={`cust-days-pending-${c.id}`}
                                  title={ls.last_service_date
                                    ? `Last service: ${ls.last_service_date}`
                                    : ls.last_invoice_date
                                      ? `Last invoice: ${ls.last_invoice_date}` : ""}
                                  className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                    days >= 30 ? "bg-[#F4C7B9] text-[#7A2E18]"
                                    : days >= 14 ? "bg-[#F9E4B7] text-[#7A5A11]"
                                    : "bg-[#DDE7F1] text-[#1F3A57]"}`}>
                              ({days}d pending)
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#8A8780]">{c.phone}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
          {form.customer_id && autofill && (
            <div className="bg-[#D4E0D1]/30 border border-[#D4E0D1] rounded-md p-4" data-testid="autofill-panel">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-medium text-[#1A1A1A] flex items-center gap-2 flex-wrap">
                    <span>{autofill.name}</span>
                    <span className="font-mono text-xs text-[#5C5A55]">{autofill.customer_code}</span>
                    {autofill.days_since_last_service != null && (
                      <span data-testid="autofill-days-pending"
                            title={autofill.last_service_date
                              ? `Last service ${autofill.last_service_date}`
                              : autofill.last_invoice_date
                                ? `Last invoice ${autofill.last_invoice_date}` : ""}
                            className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                              autofill.days_since_last_service >= 30 ? "bg-[#F4C7B9] text-[#7A2E18]"
                              : autofill.days_since_last_service >= 14 ? "bg-[#F9E4B7] text-[#7A5A11]"
                              : "bg-[#DDE7F1] text-[#1F3A57]"}`}>
                        ({autofill.days_since_last_service}d pending)
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#5C5A55]">{autofill.phone || "—"}</div>
                </div>
                {!isEdit && (
                  <button type="button" data-testid="sr-cust-change"
                          onClick={() => { setForm(f => ({...f, customer_id: ""})); setAutofill(null); }}
                          className="text-[#C25D38] text-xs">change customer</button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-xs">
                <InfoRow k="Ward" v={autofill.ward_name || "—"}/>
                <InfoRow k="LSGD" v={autofill.lsgd_name || "—"}/>
                <InfoRow k="Health Zone"
                         v={autofill.zone_name
                              ? <span className="font-medium text-[#2D5A27]">{autofill.zone_name}</span>
                              : <span className="text-[#C25D38]">⚠ not mapped</span>}/>
                <InfoRow k="Vehicle Access"
                         v={autofill.vehicle_access === true ? "Yes"
                              : autofill.vehicle_access === false ? "No" : "—"}/>
                <InfoRow k="Next Collection"
                         v={autofill.next_collection_date
                              ? <span className="font-medium text-[#2D5A27]">{autofill.next_collection_date}</span>
                              : <span className="text-[#C25D38]">⚠ no schedule</span>}/>
                <InfoRow k="Collection Days"
                         v={(autofill.collection_days || []).length
                              ? autofill.collection_days.map(d => d.slice(0, 3)).join(", ")
                              : "—"}/>
                <InfoRow k="Address" v={autofill.address || "—"}/>
                <InfoRow k="GPS"
                         v={autofill.latitude && autofill.longitude
                              ? <a href={autofill.map_url} target="_blank" rel="noreferrer" className="text-[#2D5A27] inline-flex items-center gap-1 underline">
                                  <MapPin size={10}/>{autofill.latitude.toFixed(4)}, {autofill.longitude.toFixed(4)}
                                </a>
                              : "—"}/>
              </div>
              {missingZone && (
                <div className="mt-3 bg-[#FEF2F2] border border-[#FCA5A5] rounded-md px-3 py-2 text-xs text-[#7F1D1D] flex items-start gap-2">
                  <Warning size={14} className="mt-0.5"/>
                  This customer's ward is not mapped to any Health Zone. Map it under <span className="font-semibold">Master Control → Health Zones</span> first.
                </div>
              )}
            </div>
          )}
        </Step>

        {/* STEP 2 — SERVICE TYPE */}
        <Step n={2} title="Service Type" done={!!form.service_category_type}
              disabled={!form.customer_id}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TypeCard testid="sr-type-home_service" icon={House}
                      active={form.service_category_type === "home_service"}
                      onClick={() => setForm(f => ({...f, service_category_type: "home_service", package_id: ""}))}
                      title="On-Premises" sub="Our team visits the customer's address"/>
            <TypeCard testid="sr-type-drop" icon={Truck}
                      active={form.service_category_type === "drop"}
                      onClick={() => setForm(f => ({...f, service_category_type: "drop", package_id: ""}))}
                      title="Drop" sub="Customer drops waste at our collection center"/>
          </div>
          {form.service_category_type === "drop" && altLocations.length > 0 && (
            <div className="mt-4 bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-3" data-testid="sr-alt-locations">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Other Available Drop Locations · next 14 days</div>
                <span className="text-[10px] text-[#8A8780]">{altLocations.length} options</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                {altLocations.slice(0, 20).map((al, i) => (
                  <div key={`${al.drop_location_id}-${al.date}-${i}`}
                       className="flex items-center gap-2 px-2 py-1.5 bg-white border border-[#E5E2D9] rounded text-[11px]"
                       data-testid={`sr-alt-${i}`}>
                    <Calendar size={11} className="text-[#2D5A27] shrink-0"/>
                    <span className="font-mono text-[#1A1A1A] shrink-0">
                      {new Date(al.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="flex-1 truncate font-medium text-[#1A1A1A]">{al.name}</span>
                    {al.zone_name && <span className="text-[#8A8780] truncate">{al.zone_name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Step>

        {/* STEP 3 — PRIORITY (hidden for DROP) */}
        {!isDrop && (
          <Step n={3} title="Priority" done={!!form.priority}
                disabled={!form.customer_id}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {PRIORITIES.map(p => (
                <button key={p.key} type="button" data-testid={`sr-prio-${p.key}`}
                        onClick={() => setForm(f => ({...f, priority: p.key}))}
                        className={`text-left border-2 rounded-md p-3 transition-all ${form.priority === p.key ? "bg-white shadow-sm" : "bg-[#FAFAF7] hover:bg-white"}`}
                        style={{ borderColor: form.priority === p.key ? p.tone : "#E5E2D9" }}>
                  <div className="font-medium text-sm" style={{ color: p.tone }}>{p.label}</div>
                  <div className="text-xs text-[#5C5A55] mt-1">{p.desc}</div>
                </button>
              ))}
            </div>
            {isHighOrEmergency && (
              <div className="mt-3 bg-[#FEF3C7] border border-[#FCD34D] rounded-md px-3 py-2 text-xs text-[#78350F] flex items-start gap-2">
                <Warning size={14} className="mt-0.5"/>
                {form.priority} priority — admin will confirm the collection date later.
              </div>
            )}
          </Step>
        )}

        {/* STEP 4 — WASTE CATEGORY */}
        <Step n={4} title="Waste Category" done={!!form.waste_category}
              disabled={!form.customer_id}>
          {categories.length === 0
            ? <div className="text-xs text-[#8A8780]">No waste categories configured. Add them under Service & Pricing → Waste Types.</div>
            : (
              <div className="flex flex-wrap gap-2">
                {categories.map(c => (
                  <button key={c.category} type="button" data-testid={`sr-wc-${c.category}`}
                          onClick={() => setForm(f => ({...f, waste_category: c.category, package_id: ""}))}
                          className={`h-9 px-3 rounded-full border text-xs transition-colors ${form.waste_category === c.category ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                    {c.category}
                    <span className="ml-1.5 opacity-75">({c.types.length})</span>
                  </button>
                ))}
              </div>
            )}
        </Step>

        {/* STEP 5 — PACKAGE */}
        <Step n={5} title="Package" done={!!form.package_id}
              disabled={!form.customer_id || !form.waste_category}>
          {packages.length === 0
            ? <div className="text-xs text-[#8A8780]">No packages available{form.waste_category ? ` for ${form.waste_category}` : ""}.</div>
            : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {packages.map(p => (
                  <button key={p.id} type="button" data-testid={`sr-pkg-${p.id}`}
                          onClick={() => setForm(f => ({...f, package_id: p.id}))}
                          className={`text-left border-2 rounded-md p-3 transition-all ${form.package_id === p.id ? "border-[#2D5A27] bg-[#D4E0D1]/30" : "border-[#E5E2D9] bg-white hover:bg-[#FAFAF7]"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm flex items-center gap-1.5"><PackageIcon size={12}/>{p.name}</div>
                      <div className="text-sm font-mono font-semibold text-[#2D5A27]">₹{p.price}</div>
                    </div>
                    <div className="text-[10px] text-[#8A8780] mt-1">
                      {(p.items || []).slice(0, 3).map(it => it.name || it.label || it.kind).join(" · ") || "—"}
                    </div>
                  </button>
                ))}
              </div>
            )}
        </Step>

        {/* STEP 6 — DATE */}
        <Step n={6}
              title={isHighOrEmergency ? "Date — admin will confirm" : isDrop ? "Drop Date" : "Pick a Collection Date"}
              done={isHighOrEmergency || isDrop || !!form.preferred_date}
              disabled={!form.customer_id || !form.package_id}>
          {/* Special-collection-days info banner (any-of match on this
              customer's district/LSGD/zone). Info-only per spec — user
              can still click any suggested date to fill the field. */}
          {specialDates.length > 0 && (
            <div className="mb-3 rounded-md border border-[#2D5A27]/30 bg-[#EDF7EA] px-3 py-2"
                 data-testid="sr-special-dates">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-[#1A3317] font-semibold">
                <Calendar size={12} weight="fill" /> Special collection days for this customer's zone
              </div>
              <div className="mt-2 space-y-2">
                {specialDates.map(r => (
                  <div key={r.id}>
                    <div className="text-xs font-medium text-[#1A1A1A]">
                      {r.title}
                      {(r.special_zones || []).map(sz => (
                        <span key={sz.id}
                              className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.1em] bg-[#F0EDF7] border border-[#3B2C7F]/25 text-[#3B2C7F] px-1.5 py-0.5 rounded">
                          ◆ {sz.name}
                        </span>
                      ))}
                    </div>
                    {r.notes && <div className="text-[11px] text-[#5C5A55]">{r.notes}</div>}
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {r.dates.slice(0, 8).map(d => (
                        <button key={d} type="button"
                                data-testid={`sr-special-${d}`}
                                onClick={() => setForm(f => ({...f, preferred_date: d, override_date: true}))}
                                className={`text-[11px] font-mono px-2 h-7 rounded border transition-colors ${form.preferred_date === d ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white text-[#1A3317] border-[#2D5A27]/40 hover:bg-[#D4E0D1]"}`}>
                          {d}
                        </button>
                      ))}
                      {r.dates.length > 8 && (
                        <span className="text-[11px] text-[#5C5A55] self-center">+{r.dates.length - 8}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isHighOrEmergency ? (
            <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-md px-3 py-2 text-xs text-[#78350F]">
              <CheckCircle size={14} weight="fill" className="inline text-[#78350F] mr-1"/>
              Date will be confirmed by admin after reviewing priority. No date selection needed.
            </div>
          ) : isDrop ? (
            <>
              <div className="text-xs text-[#5C5A55] mb-2 flex items-center gap-2">
                <span>Drop service — customer delivers waste at:</span>
                {autofill?.drop_location?.name && (
                  <span className="font-medium text-[#1A1A1A]">{autofill.drop_location.name}</span>
                )}
                {autofill?.drop_location?.schedule_type && (
                  <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-[#D4E0D1] text-[#2D5A27] rounded">
                    {autofill.drop_location.schedule_type === "alternate_week" ? "Alternate Week" : "Weekly"}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {(autofill?.drop_dates || []).slice(0, 5).map(d => (
                  <button key={d} type="button" data-testid={`sr-date-${d}`}
                          onClick={() => setForm(f => ({...f, preferred_date: d, override_date: false}))}
                          className={`h-9 px-3 rounded-md border text-xs font-mono transition-colors ${form.preferred_date === d ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                    <Calendar size={11} className="inline mr-1"/>{d}
                  </button>
                ))}
                {(autofill?.drop_dates || []).length === 0 && (
                  <div className="text-xs text-[#C25D38] inline-flex items-center gap-1.5">
                    <Warning size={12}/> No drop point mapped to this customer's zone.
                  </div>
                )}
              </div>
            </>
          ) : noSchedule ? (
            <>
              <div className="text-xs text-[#C25D38] mb-2 inline-flex items-center gap-1.5">
                <Warning size={12}/> Your zone has no schedule mapped. Please contact admin or choose High/Emergency service.
              </div>
              <input type="date" value={form.preferred_date} data-testid="sr-date"
                     onChange={e => setForm(f => ({...f, preferred_date: e.target.value, override_date: true}))}
                     className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            </>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                {(autofill?.next_5_dates || []).map(d => (
                  <button key={d} type="button" data-testid={`sr-date-${d}`}
                          onClick={() => setForm(f => ({...f, preferred_date: d, override_date: false}))}
                          className={`h-9 px-3 rounded-md border text-xs font-mono transition-colors ${form.preferred_date === d ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                    <Calendar size={11} className="inline mr-1"/>{d}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[#5C5A55]">or pick another date:</span>
                <input type="date" value={form.preferred_date} data-testid="sr-date-manual"
                       onChange={e => setForm(f => ({...f, preferred_date: e.target.value,
                                                      override_date: !(autofill?.next_5_dates || []).includes(e.target.value)}))}
                       className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs"/>
                {form.override_date && (
                  <span className="text-[#C25D38] text-[11px] inline-flex items-center gap-1"><Warning size={11}/>override</span>
                )}
              </div>
            </>
          )}
        </Step>

        {/* STEP 7 — NOTES (optional) */}
        <Step n={7} title="Notes (optional)" done={false}
              disabled={!form.customer_id}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <L>Preferred slot</L>
              <input placeholder="09:00-12:00" value={form.preferred_slot}
                     onChange={e => setForm(f => ({...f, preferred_slot: e.target.value}))}
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            </div>
            <div>
              <L>Estimated Weight (kg)</L>
              <input type="number" step="0.1" value={form.estimated_weight_kg}
                     onChange={e => setForm(f => ({...f, estimated_weight_kg: e.target.value}))}
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            </div>
            <div className="md:col-span-2">
              <L>Description / Notes</L>
              <textarea value={form.description} rows={2}
                        onChange={e => setForm(f => ({...f, description: e.target.value}))}
                        className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
            </div>
          </div>
        </Step>

        {/* STEP 8 — EXTRA CHARGES (optional) */}
        <Step n={8} title="Extra Charges (optional)" done={false}
              disabled={!form.package_id}>
          <div className="text-xs text-[#5C5A55] mb-3">
            Priority surcharge (if any) is added automatically. Select any
            additional charges that apply to this request.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {extraCharges.filter(ec => !ec.priority).map(ec => {
              const checked = form.extra_charge_ids.includes(ec.id);
              return (
                <label key={ec.id}
                       className={`flex items-center gap-2 px-3 py-2 border rounded-md text-xs cursor-pointer transition-colors ${checked ? "border-[#2D5A27] bg-[#D4E0D1]/30" : "border-[#E5E2D9] bg-white hover:bg-[#FAFAF7]"}`}
                       data-testid={`sr-charge-${ec.type}`}>
                  <input type="checkbox" checked={checked}
                         onChange={() => setForm(f => ({
                           ...f,
                           extra_charge_ids: checked
                             ? f.extra_charge_ids.filter(x => x !== ec.id)
                             : [...f.extra_charge_ids, ec.id],
                         }))}
                         className="accent-[#2D5A27]"/>
                  <span className="flex-1">{ec.description || ec.type}</span>
                  <span className="font-mono font-semibold text-[#2D5A27]">₹{ec.amount}</span>
                </label>
              );
            })}
          </div>
        </Step>

        {/* BILL SUMMARY */}
        {bill && (
          <div className="bg-white border-2 border-[#2D5A27] rounded-md p-5" data-testid="sr-bill-summary">
            <div className="flex items-center gap-2 mb-3">
              <CurrencyInr size={18} weight="fill" color="#2D5A27"/>
              <div className="font-heading text-base">Bill Summary</div>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-[#5C5A55]">Package — {bill.package.name}</span>
                <span className="font-mono">₹{bill.package_price.toFixed(2)}</span>
              </div>
              {bill.charges.map(ch => (
                <div key={ch.id} className="flex justify-between" data-testid={`bill-row-${ch.type}`}>
                  <span className="text-[#5C5A55]">
                    {ch.description}
                    {ch.auto && <span className="ml-1 text-[10px] text-[#8A8780]">(auto · {bill.priority})</span>}
                  </span>
                  <span className="font-mono">₹{ch.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-[#E5E2D9] pt-2 mt-2 flex justify-between font-semibold text-base">
                <span>Total</span>
                <span className="font-mono text-[#2D5A27]" data-testid="bill-total">₹{bill.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Partner marketplace options (Iter 243) */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-4 space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox"
                    checked={form.allow_partners !== false}
                    onChange={e => setForm(f => ({...f, allow_partners: e.target.checked}))}
                    data-testid="sr-allow-partners"
                    className="mt-0.5"/>
            <div>
              <div className="text-sm font-medium">Also allow authorised service partners to accept this job</div>
              <div className="text-[11px] text-[#8A8780]">
                When prepaid, the request auto-broadcasts to nearby on-duty gig partners.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={!!form.is_urgent}
                    onChange={e => setForm(f => ({...f, is_urgent: e.target.checked}))}
                    data-testid="sr-is-urgent"
                    className="mt-0.5"/>
            <div>
              <div className="text-sm font-medium">Priority: Urgent (medical)</div>
              <div className="text-[11px] text-[#8A8780]">Adds an URGENT ribbon to the partner offer card.</div>
            </div>
          </label>
        </div>

        {/* Submit */}
        <button type="submit" data-testid="sr-save"
                disabled={busy || !form.customer_id || !form.package_id
                           || (!isDrop && !isHighOrEmergency && !form.preferred_date)}
                className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
          {busy ? "Saving…" : isEdit ? "Update request" : "Confirm & create service request"}
        </button>
      </form>
    </AppShell>
  );
}

/* ---------- Small layout primitives ---------- */
function Step({ n, title, done, children, disabled }) {
  return (
    <div className={`bg-white border rounded-md p-5 ${disabled ? "opacity-60" : ""} ${done ? "border-[#D4E0D1]" : "border-[#E5E2D9]"}`}
         data-testid={`sr-step-${n}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${done ? "bg-[#2D5A27] text-white" : "bg-[#F7F6F2] text-[#5C5A55]"}`}>
          {done ? <CheckCircle size={14} weight="fill"/> : n}
        </div>
        <div className="font-heading text-sm">{title}</div>
      </div>
      {children}
    </div>
  );
}

function TypeCard({ icon: Icon, active, onClick, title, sub, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`text-left border-2 rounded-md p-4 transition-all ${active ? "border-[#2D5A27] bg-[#D4E0D1]/30" : "border-[#E5E2D9] bg-white hover:bg-[#FAFAF7]"}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={18} color={active ? "#2D5A27" : "#5C5A55"} weight="fill"/>
        <div className="font-medium">{title}</div>
      </div>
      <div className="text-xs text-[#5C5A55]">{sub}</div>
    </button>
  );
}

const L = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{children}</label>
);
const InfoRow = ({ k, v }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">{k}</span>
    <span className="text-[#2D2B26]">{v}</span>
  </div>
);
